import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FlaskConical,
  Leaf,
  Lightbulb,
  LockKeyhole,
  RadioTower,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { formatDuration, formatQuantity } from "../lib/format";
import { useResponsiveLayout } from "../lib/useResponsiveLayout";
import {
  coverageImpactWarning,
  isSurvivorAvailableForTemporaryWork,
} from "../lib/scheduling";
import type {
  Catalog,
  GameCommand,
  GameState,
  ResourceKey,
  Survivor,
  WorkTask,
} from "../types";
import { SurvivorPortrait } from "./SurvivorPortrait";

interface ResearchPanelProps {
  state: GameState;
  catalog: Catalog;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
  onOpenGenerator?: () => void;
}

const branchDetails = {
  sustainment: { name: "Sustainment", icon: Leaf },
  surface: { name: "Surface", icon: RadioTower },
  continuity: { name: "Continuity", icon: ShieldCheck },
} as const;

const branchOrder = ["sustainment", "surface", "continuity"] as const;

const eraDetails = {
  1: {
    name: "Survive",
    description: "Secure the essentials and make the first shelter viable.",
  },
  2: {
    name: "Endure",
    description: "Build the systems needed to withstand a long siege.",
  },
  3: {
    name: "Continue",
    description: "Carry a proven community beyond any single bunker.",
  },
} as const;

function projectBranch(project: Catalog["research"][string]) {
  return project.branch ?? "sustainment";
}

function ResearchGate({
  label,
  name,
  met,
}: {
  label: string;
  name: string;
  met: boolean;
}) {
  return (
    <li
      className={met ? "met" : "missing"}
      aria-label={`${label}: ${name}. ${met ? "Met" : "Missing"}.`}
    >
      {met ? (
        <CheckCircle2 aria-hidden="true" />
      ) : (
        <LockKeyhole aria-hidden="true" />
      )}
      <span>
        <small>{label}</small>
        <strong>{name}</strong>
      </span>
      <em>{met ? "Met" : "Missing"}</em>
    </li>
  );
}

function ResearcherPicker({
  state,
  survivors,
  task,
  busy,
  onCommand,
}: {
  state: GameState;
  survivors: Survivor[];
  task: WorkTask;
  busy: boolean;
  onCommand: ResearchPanelProps["onCommand"];
}) {
  return (
    <fieldset className="research-workers active-research-workers">
      <legend>
        <UsersRound />
        Assigned researchers · {task.worker_ids.length}
      </legend>
      {survivors.map((survivor) => {
        const checked = task.worker_ids.includes(survivor.id);
        const warning = checked ? null : coverageImpactWarning(state, survivor);
        const warningId = `active-research-coverage-${task.id}-${survivor.id}`;
        return (
          <label className={`${checked ? "checked" : ""} ${warning ? "has-coverage-impact" : ""}`} key={survivor.id} title={warning ?? undefined}>
            <input
              type="checkbox"
              aria-label={`${survivor.name}, ${task.skill} ${survivor.skills[task.skill]} of 5`}
              aria-describedby={warning ? warningId : undefined}
              checked={checked}
              disabled={busy}
              onChange={() => {
                void onCommand(
                  checked
                    ? { type: "unassign_survivor", survivor_id: survivor.id }
                    : {
                        type: "assign_task",
                        task_id: task.id,
                        survivor_id: survivor.id,
                      },
                );
              }}
            />
            <SurvivorPortrait survivor={survivor} />
            <span>
              <strong>{survivor.name}</strong>
              <small>
                {task.skill} {survivor.skills[task.skill]}/5
              </small>
            </span>
            {warning && <small className="coverage-impact" id={warningId}><AlertTriangle size={12} />Protected coverage may fall</small>}
          </label>
        );
      })}
    </fieldset>
  );
}

export function ResearchPanel({
  state,
  catalog,
  busy,
  onCommand,
  onOpenGenerator,
}: ResearchPanelProps) {
  const layout = useResponsiveLayout();
  const [chooserOpen, setChooserOpen] = useState(false);
  const availableProjects = Object.entries(catalog.research);
  const projectsByEra = ([1, 2, 3] as const)
    .map((era) => ({
      era,
      branches: branchOrder
        .map((branch) => ({
          branch,
          projects: availableProjects.filter(
            ([, project]) =>
              (project.era ?? 1) === era && projectBranch(project) === branch,
          ),
        }))
        .filter(({ projects }) => projects.length > 0),
    }))
    .filter(({ branches }) => branches.length > 0);
  const activeTask = state.tasks.find((task) => task.kind === "research");
  const [selectedId, setSelectedId] = useState(
    activeTask?.project_id ??
      availableProjects.find(
        ([id]) => !state.research.completed.includes(id),
      )?.[0] ??
      availableProjects[0]?.[0] ??
      "",
  );
  const [workers, setWorkers] = useState<string[]>([]);
  const selected = catalog.research[selectedId];
  const completedMilestones = state.milestones?.completed ?? [];
  const operationalRoomTypes = new Set(
    (state.site?.spaces ?? [])
      .filter(
        (space) =>
          space.room?.status === "operational"
          && space.room.enabled !== false
          && (
            space.room.operation === undefined
            || space.room.operation.power_demand === 0
            || space.room.operation.powered
          ),
      )
      .map((space) => space.room?.type)
      .filter((roomType): roomType is string => Boolean(roomType)),
  );
  const availableSurvivors = useMemo(
    () =>
      state.survivors.filter(isSurvivorAvailableForTemporaryWork),
    [state.survivors],
  );
  const availableSurvivorIds = useMemo(
    () => new Set(availableSurvivors.map((survivor) => survivor.id)),
    [availableSurvivors],
  );
  const validWorkers = useMemo(
    () => workers.filter((survivorId) => availableSurvivorIds.has(survivorId)),
    [availableSurvivorIds, workers],
  );
  useEffect(() => {
    setWorkers((current) => {
      const next = current.filter((survivorId) => availableSurvivorIds.has(survivorId));
      return next.length === current.length ? current : next;
    });
  }, [availableSurvivorIds]);
  const activeResearcherCandidates = useMemo(
    () =>
      activeTask
        ? state.survivors.filter(
            (survivor) =>
              survivor.alive
              && survivor.status === "home"
              && (activeTask.worker_ids.includes(survivor.id) || isSurvivorAvailableForTemporaryWork(survivor)),
          )
        : [],
    [activeTask, state.survivors],
  );
  const affordable = selected
    ? (Object.entries(selected.cost) as Array<[ResourceKey, number]>).every(
        ([resource, amount]) => state.resources[resource] >= amount,
      )
    : false;
  const selectedIsActive = activeTask?.project_id === selectedId;
  const missingResearch = selected?.requires.filter(
    (requirement) => !state.research.completed.includes(requirement),
  ) ?? [];
  const missingMilestones = (selected?.requires_milestones ?? []).filter(
    (requirement) => !completedMilestones.includes(requirement),
  );
  const missingRooms = (selected?.requires_rooms ?? []).filter(
    (roomType) => !operationalRoomTypes.has(roomType),
  );
  const missingPower = Boolean(selected?.requires_power && state.site?.power !== "stable");
  const generatorSpaces = (state.site?.spaces ?? []).filter(
    (space) => space.room?.type === "generator",
  );
  const enabledOperationalGenerator = generatorSpaces.some(
    (space) =>
      space.room?.status === "operational"
      && space.room.enabled !== false,
  );
  const generatorOperatorAssigned = generatorSpaces.some(
    (space) =>
      space.room?.status === "operational"
      && space.room.enabled !== false
      && (
        (space.room.operation?.staffing?.active_worker_ids.length ?? 0) > 0
        || (space.room.operation?.staffing?.relief_worker_ids.length ?? 0) > 0
      ),
  );
  const stablePowerChecks = [
    {
      label: "Enabled, operational generator",
      met: enabledOperationalGenerator,
    },
    { label: "Fuel available", met: (state.resources.fuel ?? 0) > 0 },
    {
      label: "Generator operator assigned",
      met: generatorOperatorAssigned,
    },
    {
      label: "No enabled room is being load-shed",
      met: (state.site?.power_grid?.shed_space_ids.length ?? 0) === 0,
    },
  ];
  const selectedLocked = Boolean(
    missingResearch.length || missingMilestones.length || missingRooms.length || missingPower,
  );
  const totalGateCount = selected
    ? selected.requires.length +
      (selected.requires_milestones ?? []).length +
      (selected.requires_rooms ?? []).length +
      (selected.requires_power ? 1 : 0)
    : 0;
  const missingGateCount =
    missingResearch.length +
    missingMilestones.length +
    missingRooms.length +
    (missingPower ? 1 : 0);
  const selectedBranch = selected
    ? branchDetails[projectBranch(selected)]
    : branchDetails.sustainment;
  const SelectedBranchIcon = selectedBranch.icon;

  return (
    <section className="research-layout" aria-busy={busy}>
      <details
        className="research-tree panel-surface"
        onToggle={(event) => {
          if (layout.narrow) setChooserOpen(event.currentTarget.open);
        }}
        open={!layout.narrow || chooserOpen}
      >
        <summary className="mobile-picker-summary">
          <span><small>Selected project</small><strong>{layout.narrow ? selected?.name ?? "Research board" : "Project chooser"}</strong></span>
          <span>Choose project</span>
          <ChevronDown aria-hidden="true" />
        </summary>
        <div className="research-tree-content">
        <header>
          <span className="eyebrow">Shared knowledge</span>
          <h2>Research board</h2>
          <p>
            Everything learned belongs to the community and survives the loss
            of a bunker.
          </p>
        </header>
        <nav className="research-project-list" aria-label="Research projects">
          {projectsByEra.map(({ era, branches }) => (
            <section
              className={`research-era research-era-${era}`}
              aria-labelledby={`research-era-${era}`}
              key={era}
            >
              <header>
                <div>
                  <span>Era {era}</span>
                  <h3 id={`research-era-${era}`}>{eraDetails[era].name}</h3>
                </div>
                <p>{eraDetails[era].description}</p>
              </header>
              {branches.map(({ branch: branchId, projects }) => {
                const branch = branchDetails[branchId];
                const BranchIcon = branch.icon;
                return (
                  <section
                    className={`research-branch research-branch-${branchId}`}
                    aria-labelledby={`research-era-${era}-${branchId}`}
                    key={branchId}
                  >
                    <header className="research-branch-heading">
                      <BranchIcon aria-hidden="true" />
                      <h4 id={`research-era-${era}-${branchId}`}>
                        {branch.name}
                      </h4>
                      <span>
                        {projects.length} project{projects.length === 1 ? "" : "s"}
                      </span>
                    </header>
                    <div className="research-branch-projects">
                      {projects.map(([id, project]) => {
                        const complete = state.research.completed.includes(id);
                        const carriedProgress = Math.max(
                          0,
                          Math.min(80, state.research.progress?.[id] ?? 0),
                        );
                        const locked =
                          project.requires.some(
                            (requirement) =>
                              !state.research.completed.includes(requirement),
                          ) ||
                          (project.requires_milestones ?? []).some(
                            (requirement) =>
                              !completedMilestones.includes(requirement),
                          ) ||
                          (project.requires_rooms ?? []).some(
                            (roomType) => !operationalRoomTypes.has(roomType),
                          ) ||
                          Boolean(
                            project.requires_power &&
                              state.site?.power !== "stable",
                          );
                        const active = activeTask?.project_id === id;
                        const status = active
                          ? "Active"
                          : complete
                            ? "Retained"
                            : locked
                              ? "Locked"
                              : carriedProgress > 0
                                ? "Carried"
                              : "Ready";
                        const detail = active
                          ? formatDuration(activeTask?.seconds_left)
                          : complete
                            ? "Knowledge retained"
                            : locked
                              ? "Prerequisite required"
                              : carriedProgress > 0
                                ? `${carriedProgress}% retained progress`
                              : project.field;
                        return (
                          <button
                            type="button"
                            key={id}
                            aria-label={`${project.name}, ${branch.name} branch, ${status}, ${detail}`}
                            aria-pressed={selectedId === id}
                            className={`${selectedId === id ? "selected" : ""} ${complete ? "complete" : ""} ${carriedProgress > 0 && !complete ? "carried" : ""}`}
                            onClick={() => {
                              setSelectedId(id);
                              setWorkers([]);
                              setChooserOpen(false);
                            }}
                          >
                            <span aria-hidden="true">
                              {complete ? (
                                <CheckCircle2 />
                              ) : locked ? (
                                <LockKeyhole />
                              ) : (
                                <FlaskConical />
                              )}
                            </span>
                            <i>
                              <strong>{project.name}</strong>
                              <small>
                                <b>{status}</b>
                                <span>{detail}</span>
                              </small>
                            </i>
                            {carriedProgress > 0 && !complete && (
                              <progress
                                className="research-carry-meter"
                                max={100}
                                value={carriedProgress}
                                aria-label={`${project.name}: ${carriedProgress}% progress retained through relocation`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </section>
          ))}
        </nav>
        </div>
      </details>
      <article className="research-detail panel-surface">
        {selected ? (
          <>
            <header>
              <div className="research-emblem">
                <Lightbulb />
              </div>
              <div>
                <span className="eyebrow">{selected.field} study</span>
                <h2>{selected.name}</h2>
                <p>{selected.description}</p>
              </div>
            </header>
            <div className="research-effect">
              <span>Expected result</span>
              <strong>{selected.effect}</strong>
            </div>
            {(state.research.progress?.[selectedId] ?? 0) > 0
              && !state.research.completed.includes(selectedId) && (
              <section className="research-continuity-progress" aria-label="Portable research progress">
                <div>
                  <span className="eyebrow">Recovered field notes</span>
                  <strong>{Math.min(80, state.research.progress?.[selectedId] ?? 0)}% carried forward</strong>
                  <p>
                    This work survived the last relocation. Its remaining material cost and laboratory time are reduced.
                  </p>
                </div>
                <progress
                  max={100}
                  value={Math.min(80, state.research.progress?.[selectedId] ?? 0)}
                  aria-label={`${selected.name}: ${Math.min(80, state.research.progress?.[selectedId] ?? 0)}% portable progress`}
                />
              </section>
            )}
            <div
              className="research-classification"
              aria-label="Project classification"
            >
              <span>
                <small>Era</small>
                <strong>
                  {selected.era ?? 1} · {eraDetails[selected.era ?? 1].name}
                </strong>
              </span>
              <span>
                <SelectedBranchIcon aria-hidden="true" />
                <small>Branch</small>
                <strong>{selectedBranch.name}</strong>
              </span>
            </div>
            <section
              className="research-gate-group"
              aria-labelledby="research-requirements-title"
            >
              <header>
                <div>
                  <span className="eyebrow">Access conditions</span>
                  <h3 id="research-requirements-title">Requirements</h3>
                </div>
                <strong
                  className={missingGateCount > 0 ? "blocked" : "ready"}
                >
                  {missingGateCount > 0
                    ? `${missingGateCount} missing`
                    : "Ready"}
                </strong>
              </header>
              <ul className="research-gates">
              {selected.requires.map((requirement) => (
                <ResearchGate
                  key={requirement}
                  label="Prior research"
                  name={catalog.research[requirement]?.name ?? requirement}
                  met={!missingResearch.includes(requirement)}
                />
              ))}
              {(selected.requires_milestones ?? []).map((requirement) => (
                <ResearchGate
                  key={requirement}
                  label="Milestone"
                  name={catalog.milestones?.[requirement]?.name ?? requirement.replaceAll("_", " ")}
                  met={!missingMilestones.includes(requirement)}
                />
              ))}
              {(selected.requires_rooms ?? []).map((roomType) => (
                <ResearchGate
                  key={roomType}
                  label="Operational room"
                  name={catalog.rooms[roomType]?.short_name ?? roomType.replaceAll("_", " ")}
                  met={!missingRooms.includes(roomType)}
                />
              ))}
              {selected.requires_power && (
                <ResearchGate
                  label="Bunker system"
                  name="Stable power"
                  met={!missingPower}
                />
              )}
              {totalGateCount === 0 && (
                <ResearchGate label="Prerequisites" name="None" met />
              )}
              </ul>
              {selected.requires_power && missingPower && (
                <section
                  className="stable-power-guide"
                  aria-labelledby="stable-power-guide-title"
                >
                  <div>
                    <span className="eyebrow">How to meet this requirement</span>
                    <h4 id="stable-power-guide-title">Establish stable power</h4>
                    <p>
                      Repair and enable a generator, give it fuel, then assign a
                      survivor to operate it. Every enabled room must receive power.
                    </p>
                  </div>
                  <ul>
                    {stablePowerChecks.map((check) => (
                      <li className={check.met ? "met" : "missing"} key={check.label}>
                        {check.met ? (
                          <CheckCircle2 aria-hidden="true" />
                        ) : (
                          <LockKeyhole aria-hidden="true" />
                        )}
                        <span>{check.label}</span>
                        <em>{check.met ? "Ready" : "Needed"}</em>
                      </li>
                    ))}
                  </ul>
                  <p className="stable-power-note">
                    Power priority only decides which room loses power; it cannot
                    create capacity. Switch off nonessential rooms or add generator
                    capacity until every enabled room is served.
                  </p>
                  {onOpenGenerator && (
                    <button type="button" onClick={onOpenGenerator}>
                      Open generator controls
                      <ArrowRight aria-hidden="true" />
                    </button>
                  )}
                </section>
              )}
            </section>
            {selected.unlocks.length > 0 && (
              <div className="unlock-list">
                <span>Unlocks</span>
                {selected.unlocks.map((room) => (
                  <b key={room}>{catalog.rooms[room]?.name ?? room}</b>
                ))}
              </div>
            )}
            <div className="research-requirements">
              <span>
                <Clock3 />
                {formatDuration(selected.seconds)}
              </span>
              {(Object.entries(selected.cost) as Array<[ResourceKey, number]>).map(
                ([resource, amount]) => (
                  <span
                    className={
                      state.resources[resource] < amount ? "missing" : ""
                    }
                    key={resource}
                  >
                    {catalog.resources[resource].name}
                    <strong>{formatQuantity(amount)}</strong>
                  </span>
                ),
              )}
            </div>
            {state.research.completed.includes(selectedId) ? (
              <div className="research-complete-state">
                <CheckCircle2 />
                <strong>Research retained</strong>
                <p>
                  The community can carry this knowledge to every future
                  bunker.
                </p>
              </div>
            ) : activeTask && selectedIsActive ? (
              <>
                <div className="active-research-card" aria-live="polite">
                  <FlaskConical />
                  <span>
                    <strong>
                      {catalog.research[activeTask.project_id ?? ""]?.name}
                    </strong>
                    <small>
                      {activeTask.worker_ids.length === 0
                        ? "Paused — assign a researcher to resume"
                        : `${formatDuration(activeTask.seconds_left)} remaining`}
                    </small>
                  </span>
                  <i>
                    {activeTask.worker_ids
                      .map(
                        (id) =>
                          state.survivors
                            .find((item) => item.id === id)
                            ?.name.split(" ")[0],
                      )
                      .filter(Boolean)
                      .join(", ") || "Nobody assigned"}
                  </i>
                </div>
                <ResearcherPicker
                  state={state}
                  survivors={activeResearcherCandidates}
                  task={activeTask}
                  busy={busy}
                  onCommand={onCommand}
                />
                <button
                  type="button"
                  className="secondary-button destructive-button"
                  disabled={busy}
                  onClick={() =>
                    onCommand({ type: "cancel_task", task_id: activeTask.id })
                  }
                >
                  Cancel research and recover unused materials
                </button>
              </>
            ) : activeTask ? (
              <div className="active-research-card">
                <FlaskConical />
                <span>
                  <strong>Research capacity occupied</strong>
                  <small>
                    {catalog.research[activeTask.project_id ?? ""]?.name} is in
                    progress.
                  </small>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setSelectedId(activeTask.project_id ?? selectedId)}
                >
                  Manage
                </button>
              </div>
            ) : (
              <>
                <fieldset className="research-workers">
                  <legend>
                    <UsersRound />
                    Choose researchers · {validWorkers.length}
                  </legend>
                  {availableSurvivors.map((survivor) => {
                    const checked = validWorkers.includes(survivor.id);
                    const proposedSelection = checked
                      ? validWorkers
                      : [...validWorkers, survivor.id];
                    const warning = coverageImpactWarning(
                      state,
                      survivor,
                      proposedSelection,
                    );
                    const warningId = `research-coverage-${selectedId}-${survivor.id}`;
                    return (
                      <label className={`${checked ? "checked" : ""} ${warning ? "has-coverage-impact" : ""}`} key={survivor.id} title={warning ?? undefined}>
                        <input
                          type="checkbox"
                          aria-label={`${survivor.name}, ${selected.field} ${survivor.skills[selected.field]} of 5`}
                          aria-describedby={warning ? warningId : undefined}
                          checked={checked}
                          disabled={busy}
                          onChange={() =>
                            setWorkers(
                              checked
                                ? validWorkers.filter((id) => id !== survivor.id)
                                : [...validWorkers, survivor.id],
                            )
                          }
                        />
                        <SurvivorPortrait survivor={survivor} />
                        <span>
                          <strong>{survivor.name}</strong>
                          <small>
                            {selected.field} {survivor.skills[selected.field]}/5
                          </small>
                        </span>
                        {warning && <small className="coverage-impact" id={warningId}><AlertTriangle size={12} />Protected coverage may fall</small>}
                      </label>
                    );
                  })}
                </fieldset>
                <button
                  type="button"
                  className="action-button"
                  disabled={
                    busy ||
                    !affordable ||
                    validWorkers.length === 0 ||
                    selectedLocked
                  }
                  onClick={() =>
                    onCommand({
                      type: "start_research",
                      project_id: selectedId,
                      worker_ids: validWorkers,
                    })
                  }
                >
                  <FlaskConical />
                  Begin research
                  <ArrowRight />
                </button>
              </>
            )}
          </>
        ) : null}
      </article>
    </section>
  );
}
